/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

// ============================================================================
// CALCULATION HELPERS - Safe Arithmetic Operations
// ============================================================================
// Provides null-safe arithmetic operations to prevent NaN/Infinity errors
// in cost calculations throughout the estimate creation pipeline.

/**
 * Safely multiply multiple values, treating null/undefined as 1 (identity).
 * Returns 0 if any value is NaN or the result is non-finite.
 *
 * @example
 * safeMultiply(10, 2, 1.5) // 30
 * safeMultiply(10, null, 2) // 20 (null treated as 1)
 * safeMultiply(10, undefined) // 10
 */
export function safeMultiply(...values: (number | null | undefined)[]): number {
  return values.reduce<number>((acc, val) => {
    const num = val ?? 1;
    if (typeof num !== "number" || isNaN(num) || !isFinite(num)) return acc;
    const result = acc * num;
    return isFinite(result) ? result : acc;
  }, 1);
}

/**
 * Safely multiply values with 0 as the identity for null/undefined.
 * Use this when null/undefined should result in 0 (e.g., quantity * cost).
 *
 * @example
 * safeMultiplyWithZero(10, 2) // 20
 * safeMultiplyWithZero(10, null) // 0 (null treated as 0)
 * safeMultiplyWithZero(undefined, 5) // 0
 */
export function safeMultiplyWithZero(
  a: number | null | undefined,
  b: number | null | undefined,
): number {
  const safeA = a ?? 0;
  const safeB = b ?? 0;

  if (typeof safeA !== "number" || typeof safeB !== "number") {
    return 0;
  }

  if (isNaN(safeA) || isNaN(safeB)) {
    return 0;
  }

  const result = safeA * safeB;

  if (!isFinite(result)) {
    return 0;
  }

  return roundCurrency(result);
}

// ============================================================================
// ROUNDING PRECISION STANDARDS
// ============================================================================
// These constants define rounding precision across the entire platform.
// DB schema: quantity numeric(12,4), costs numeric(12,2)
// All calculation code should use these constants, not ad-hoc Math.round.

export const PRECISION = {
  /** Quantities: 2 decimals for display and calculation (e.g., 6.67 hrs, 10.50 days) */
  QUANTITY: 2,
  /** Currency: 2 decimals (e.g., $150.00) — matches DB numeric(12,2) */
  CURRENCY: 2,
  /** Percentages: 2 decimals (e.g., 6.30%) */
  PERCENTAGE: 2,
  /** Production rates: 4 decimals for precision in intermediate calculations */
  RATE: 4,
} as const;

/** Round currency to nearest $0.05 (nickel) */
export function roundCurrency(value: number): number {
  return Math.round(value * 20) / 20;
}

/** Round quantity to standard precision (2 decimals) */
export function roundQuantity(value: number): number {
  const factor = Math.pow(10, PRECISION.QUANTITY);
  return Math.round(value * factor) / factor;
}

/** Round hours up to nearest 0.5 (never bill less than a half hour) */
export function roundHours(value: number): number {
  if (value <= 0) return 0;
  return Math.ceil(value * 2) / 2;
}

/** Round percentage to standard precision (2 decimals) */
export function roundPercentage(value: number): number {
  const factor = Math.pow(10, PRECISION.PERCENTAGE);
  return Math.round(value * factor) / factor;
}

/** Calculate total cost: qty * unit_cost, properly rounded */
export function calcTotalCost(quantity: number, unitCost: number): number {
  return roundCurrency(quantity * unitCost);
}

/**
 * Safely divide two numbers with fallback for zero/invalid denominators.
 *
 * @param numerator - The dividend
 * @param denominator - The divisor
 * @param defaultValue - Value to return if division is invalid (default: 0)
 *
 * @example
 * safeDivide(10, 2) // 5
 * safeDivide(10, 0) // 0 (default)
 * safeDivide(10, 0, -1) // -1 (custom default)
 * safeDivide(null, 5) // 0
 */
export function safeDivide(
  numerator: number | null | undefined,
  denominator: number | null | undefined,
  defaultValue: number = 0,
): number {
  const num = numerator ?? 0;
  const den = denominator ?? 0;

  if (typeof num !== "number" || typeof den !== "number") {
    return defaultValue;
  }

  if (den === 0 || isNaN(den) || !isFinite(den)) {
    return defaultValue;
  }

  if (isNaN(num) || !isFinite(num)) {
    return defaultValue;
  }

  const result = num / den;
  return isFinite(result) ? result : defaultValue;
}

/**
 * Safely sum multiple values, treating null/undefined as 0.
 *
 * @example
 * safeSum(1, 2, 3) // 6
 * safeSum(1, null, 3) // 4
 * safeSum() // 0
 */
export function safeSum(...values: (number | null | undefined)[]): number {
  return values.reduce<number>((acc, val) => {
    const num = val ?? 0;
    if (typeof num !== "number" || isNaN(num) || !isFinite(num)) return acc;
    const result = acc + num;
    return isFinite(result) ? result : acc;
  }, 0);
}

/**
 * Clamp a value between min and max bounds.
 *
 * @example
 * clamp(5, 0, 10) // 5
 * clamp(-5, 0, 10) // 0
 * clamp(15, 0, 10) // 10
 */
export function clamp(value: number, min: number, max: number): number {
  if (typeof value !== "number" || isNaN(value)) {
    return min;
  }
  return Math.max(min, Math.min(max, value));
}

/**
 * Validate that a number is a valid positive value for calculations.
 * Returns the value if valid, or null if invalid.
 *
 * @example
 * validatePositive(10) // 10
 * validatePositive(-5) // null
 * validatePositive(0) // null (0 is not positive)
 * validatePositive(0, true) // 0 (allow zero)
 */
export function validatePositive(
  value: number | null | undefined,
  allowZero: boolean = false,
): number | null {
  if (value === null || value === undefined) {
    return null;
  }

  if (typeof value !== "number" || isNaN(value) || !isFinite(value)) {
    return null;
  }

  if (allowZero) {
    return value >= 0 ? value : null;
  }

  return value > 0 ? value : null;
}

/**
 * Ensure a value is at least a minimum amount.
 * Useful for factors like waste_factor that should be >= 1.0
 *
 * @example
 * ensureMinimum(1.5, 1.0) // 1.5
 * ensureMinimum(0.5, 1.0) // 1.0
 * ensureMinimum(null, 1.0) // 1.0
 */
export function ensureMinimum(
  value: number | null | undefined,
  minimum: number,
): number {
  if (value === null || value === undefined) {
    return minimum;
  }

  if (typeof value !== "number" || isNaN(value) || !isFinite(value)) {
    return minimum;
  }

  return value >= minimum ? value : minimum;
}

/**
 * Round a number to a specific number of decimal places.
 * Returns 0 for invalid inputs.
 *
 * @example
 * roundTo(3.14159, 2) // 3.14
 * roundTo(100.5, 0) // 101
 * roundTo(null, 2) // 0
 */
export function roundTo(
  value: number | null | undefined,
  decimals: number = 2,
): number {
  if (value === null || value === undefined) {
    return 0;
  }

  if (typeof value !== "number" || isNaN(value) || !isFinite(value)) {
    return 0;
  }

  const factor = Math.pow(10, decimals);
  return Math.round(value * factor) / factor;
}

/**
 * Check if a value is a valid number (not null, undefined, NaN, or Infinity).
 *
 * @example
 * isValidNumber(5) // true
 * isValidNumber(0) // true
 * isValidNumber(null) // false
 * isValidNumber(NaN) // false
 * isValidNumber(Infinity) // false
 */
export function isValidNumber(value: unknown): value is number {
  return typeof value === "number" && !isNaN(value) && isFinite(value);
}

/**
 * Get a numeric value with a default fallback.
 * Unlike nullish coalescing, this also handles NaN and Infinity.
 *
 * @example
 * getNumericValue(5, 0) // 5
 * getNumericValue(null, 10) // 10
 * getNumericValue(NaN, 10) // 10
 * getNumericValue(Infinity, 10) // 10
 */
export function getNumericValue(
  value: number | null | undefined,
  defaultValue: number,
): number {
  if (value === null || value === undefined) {
    return defaultValue;
  }

  if (typeof value !== "number" || isNaN(value) || !isFinite(value)) {
    return defaultValue;
  }

  return value;
}
